import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './HotelConfirmation.css';
import { apiRequest, parseResponse, extractHotelsList } from './utils/api';
import { useLanguage } from './i18n/LanguageContext';

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const hotelPin = (selected, label) => {
  const color = selected ? '#2f6bff' : '#f14d9a';
  const safe = escapeHtml(label || '');
  return L.divIcon({
    className: `hotel-pin${selected ? ' is-selected' : ''}`,
    html: `<svg class="pin-mark" width="22" height="28" viewBox="0 0 22 28" aria-hidden="true"><path d="M11 0C5.1 0 .2 4.8.2 10.7.2 18.2 11 28 11 28s10.8-9.8 10.8-17.3C21.8 4.8 16.9 0 11 0z" fill="${color}"/><circle cx="11" cy="10.5" r="3.6" fill="#ffffff"/></svg>${safe ? `<span class="pin-label">${safe}</span>` : ''}`,
    iconSize: [190, 64],
    iconAnchor: [11, 28]
  });
};

const FitHotels = ({ points }) => {
  const map = useMap();
  const key = points.map((point) => point.join(',')).join('|');
  useEffect(() => {
    const parsed = key
      ? key.split('|').map((item) => item.split(',').map(Number))
      : [];
    if (!parsed.length) return;
    if (parsed.length === 1) {
      map.setView(parsed[0], 15);
      return;
    }
    const bounds = L.latLngBounds(parsed);
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [72, 72], maxZoom: 15 });
    }
  }, [map, key]);
  return null;
};

const coordinate = (...values) => {
  for (const value of values) {
    const number = parseFloat(value);
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const formatAddress = (hotel) => {
  if (!hotel) return '';
  const parts = [hotel.description, hotel.address, hotel.location, hotel.city, hotel.state, hotel.pincode || hotel.zipCode]
    .map((part) => (part == null ? '' : String(part).trim()))
    .filter(Boolean);
  const unique = [];
  parts.forEach((part) => {
    if (!unique.some((item) => item.toLowerCase() === part.toLowerCase())) unique.push(part);
  });
  const name = hotel.hotelName ? String(hotel.hotelName).trim() : '';
  const joined = unique.join(', ');
  if (name && !joined.toLowerCase().includes(name.toLowerCase())) {
    return joined ? `${name}, ${joined}` : name;
  }
  return joined || name;
};

const hotelPoint = (hotel) => {
  if (!hotel) return [null, null];
  return [
    coordinate(hotel.latitude, hotel.lat),
    coordinate(hotel.longitude, hotel.lng, hotel.lon)
  ];
};

const FocusSelected = ({ point, token }) => {
  const map = useMap();
  const lat = point[0];
  const lng = point[1];
  useEffect(() => {
    if (lat === null || lng === null) return;
    map.flyTo([lat, lng], Math.max(map.getZoom(), 14), { duration: 0.45 });
  }, [map, token, lat, lng]);
  return null;
};

const EditHotelPage = ({ latitude, longitude, onConfirmLocation, onBack }) => {
  const { t } = useLanguage();
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const fetchHotels = useCallback(async () => {
    setLoadError(null);
    setLoading(true);
    const readHotels = async (path) => {
      const response = await apiRequest(path, { method: 'GET', skipAuth: true });
      if (!response.ok) return null;
      const parsed = await parseResponse(response);
      const list = extractHotelsList(parsed);
      return Array.isArray(list) ? list : null;
    };
    try {
      let hotelsData = await readHotels('/hotels');
      if (hotelsData === null && latitude != null && longitude != null) {
        hotelsData = await readHotels(`/hotels/nearby?latitude=${latitude}&longitude=${longitude}`);
      }
      if (hotelsData === null) {
        setLoadError('We could not load hotels. Check your connection and tap Retry.');
        setHotels([]);
        return;
      }
      setHotels(hotelsData);
      let savedId = null;
      try {
        const saved = JSON.parse(localStorage.getItem('selectedHotel') || '{}');
        savedId = saved.id || saved.hotelId || null;
      } catch (error) {
        savedId = null;
      }
      if (savedId != null) {
        const current = hotelsData.find((item) => String(item.id || item.hotelId) === String(savedId));
        setSelectedHotel(current || null);
      }
    } catch (err) {
      console.error(err);
      setLoadError('We could not load hotels. Check your connection and tap Retry.');
      setHotels([]);
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude]);

  useEffect(() => {
    fetchHotels();
  }, [retryKey, fetchHotels]);

  const filteredHotels = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return hotels;
    return hotels.filter((hotel) => {
      const haystack = [hotel.hotelName, hotel.description, hotel.address, hotel.city, hotel.location]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [hotels, query]);

  const chooseHotel = (hotel) => {
    setSelectedHotel(hotel);
    setQuery(hotel.hotelName || '');
    setSearchOpen(false);
  };

  const points = filteredHotels
    .map((item) => hotelPoint(item))
    .filter((point) => point[0] !== null && point[1] !== null);
  const selectedPoint = hotelPoint(selectedHotel);
  const userPoint = [coordinate(latitude), coordinate(longitude)];
  const center = selectedPoint[0] !== null
    ? selectedPoint
    : (userPoint[0] !== null ? userPoint : (points[0] || [28.6139, 77.209]));
  const selectedToken = selectedHotel ? String(selectedHotel.id || selectedHotel.hotelId || selectedHotel.hotelName) : '';

  return (
    <div className="hotel-confirmation-screen is-map">
      {onBack && (
        <button type="button" className="location-show-back" onClick={onBack} aria-label="Go back">
          <img src={process.env.PUBLIC_URL + '/images/back_button.svg'} alt="" />
        </button>
      )}

      <div className="hotel-map-search">
        <input
          type="search"
          value={query}
          placeholder={t('Search hotels')}
          aria-label={t('Search hotels')}
          onChange={(event) => {
            setQuery(event.target.value);
            setSearchOpen(true);
          }}
          onFocus={() => setSearchOpen(true)}
        />
        {searchOpen && query.trim() && (
          <div className="hotel-map-results">
            {filteredHotels.length === 0 ? (
              <p className="hotel-map-empty">{t('No results found')}</p>
            ) : (
              filteredHotels.slice(0, 20).map((hotel, index) => (
                <button
                  type="button"
                  key={hotel.id || hotel.hotelId || `${hotel.hotelName}-${index}`}
                  className="hotel-map-result"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => chooseHotel(hotel)}
                >
                  <strong>{hotel.hotelName}</strong>
                  {hotel.description && <span>{hotel.description}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {(loading || loadError) && (
        <div className="hotel-map-status">
          <p>{loading ? t('Finding nearby hotels...') : t(loadError)}</p>
          {!loading && (
            <button type="button" onClick={() => setRetryKey((key) => key + 1)}>
              {t('Retry')}
            </button>
          )}
        </div>
      )}

      <div className="location-show-map">
        <MapContainer
          center={center}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
          scrollWheelZoom
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {points.length > 0 && <FitHotels points={points} />}
          {selectedToken && <FocusSelected point={selectedPoint} token={selectedToken} />}
          {filteredHotels.map((item, index) => {
            const [lat, lng] = hotelPoint(item);
            if (lat === null || lng === null) return null;
            const selected = item === selectedHotel
              || (selectedHotel && String(item.id || item.hotelId) === String(selectedHotel.id || selectedHotel.hotelId));
            return (
              <Marker
                key={item.id || item.hotelId || `${lat}-${lng}-${index}`}
                position={[lat, lng]}
                zIndexOffset={selected ? 500 : 0}
                icon={hotelPin(selected, item.hotelName || '')}
                eventHandlers={{ click: () => setSelectedHotel(item) }}
              />
            );
          })}
        </MapContainer>
      </div>

      <div className="location-show-sheet">
        <p className="selected-kicker">{t('Selected Address')}</p>
        <p className="selected-address">
          {selectedHotel ? formatAddress(selectedHotel) : t('Select a hotel on the map')}
        </p>
        <button
          className="confirm-btn"
          onClick={() => onConfirmLocation(selectedHotel)}
          disabled={!selectedHotel}
        >
          <span className="confirm-btn-text">{t('Confirm Location')}</span>
        </button>
      </div>
    </div>
  );
};

export default EditHotelPage;

